<template>
    <div v-if="show">
        <div class="overlay" @click="onCloseCallback"></div>
        <div class="modal skill-rating">
            <div class="modal-content">
                <div class="modal-title">
                    <span class="text">Skill Rating</span>
                </div>
                <p>
                    A Skill Rating (SR) is a value that represents a player's proficiency level in the game.
                    It is determined by how well that player performs against other players.
                </p>
                <p>
                    Every player starts with a base SR of 2000.
                </p>
                <p>
                    Every win/loss increases/decreases the SR by 30 points.
                </p>
                <p>
                    The SR gained or lost in a game may be modified by the difference
                    in SR between the players based on the probability of player A
                    winning against player B.
                </p>
                <div>
                    The formula used for calculating the win probability is:

                    <span class="equation">
                            <span class="equation-lhs">E<sub>A</sub></span>
                            <span class="equals-sign"> = </span>
                        <span class="equation-rhs fraction">
                            <span class="numerator">1</span>
                                <span class="denominator">
                                <span class="power">1 + 10</span>
                                <sup class="fraction">
                                    <span class="numerator">
                                        R<sub>B</sub> -
                                        R<sub>A</sub>
                                    </span>
                                    <span class="denominator">1500</span>
                                </sup>
                            </span>
                        </span>
                        </span>

                        <ul class="equation-legend">
                            <li>
                                E - The probability of Player A winning
                                </li>
                            <li>
                                R - The rankings of Player A/B
                                </li>
                                <li>
                                    1500 - The difference in SR at which point
                                    Player A is 10 times more likely to win against Player B
                                </li>
                        </ul>
				</div>
                <div>
                    <strong>Example:</strong><br/><br/>

                    If Player A with 2500 SR wins against Player B with 2250 SR:

                    <ul>
                        <li>
                            The SR difference between the players is 250
                        </li>
                        <li>
                            The probability of Player A winning is 59.00%
                        </li>
                        <li>
                            The winner will gain <strong>25 SR</strong>:
                            <div class="sr-breakdown">
                                +25 SR = 60 SR (base) * (1 - 0.59)
                            </div>
                        </li>
                        <li>
                            The loser will lose <strong>23 SR</strong>:
                            <div class="sr-breakdown">
                                -23 SR = The 25 SR as calculated above, minus 2 points as a "loss mitigation" factor.
                            </div>
                        </li>
                        <li>
                            The change in SR ranges from 10 - 45 points (or 5 - 45 for Grandmasters).
                        </li>
                    </ul>

                    <span class="example-note">
                        Had Player B won with a win probability of 41%, they would
                        have gained 36 SR and Player A would have lost 34 SR.
                    </span>
                </div>
                <div class="modal-title">
                    <span class="text">Rank</span>
                </div>
                <div>
                    <p>
                        A Rank is a badge assigned to a specific Skill Rating range.
                    </p>
                    <div>
                        <ul>
                            <li>
                                <strong>Bronze</strong> (<rank :skillRating="1000" />) 0 to 1499 SR
                            </li>
                            <li>
                                <strong>Silver</strong> (<rank :skillRating="1500" />) 1500 to 1999 SR
                            </li>
                            <li>
                            <strong>Gold</strong> (<rank :skillRating="2000" />) 2000 to 2499 SR
                            </li>
                            <li>
                                <strong>Platinum</strong> (<rank :skillRating="2500" />) 2500 to 2999 SR
                            </li>
                            <li>
                                <strong>Diamond</strong> (<rank :skillRating="3000" />) 3000 to 3499 SR
                            </li>
                            <li>
                                <strong>Masters</strong> (<rank :skillRating="3500" />) 3500 to 3999 SR
                            </li>
                            <li>
                                <strong>Grandmaster</strong> (<rank :skillRating="4000" />) 4000 to 5000 SR
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <span class="close-modal-btn" @click="onCloseCallback">
                <fa-icon class="close-modal-icon" icon="times" />
            </span>
        </div>
    </div>
</template>

<script>

export default {
    name: 'SkillRatingModal',
    props: ['show', 'onCloseCallback'],
};
</script>

<style scoped lang="scss">
    @use '@/styles/styles.scss' as *;

    .modal {

        &.skill-rating {
            font-size: .9em;
            min-width: 350px;
            height: 80%;

            .modal-content {
                height: 100%;
                overflow: auto;
            }

            .modal-title {
                text-align: center;
            }

            ul {
                margin-left: 30px;
                margin-top: 15px;
                list-style-type: disc;

                li {
                    margin-bottom: 10px;

                    &:last-of-type {
                        margin-bottom: 0;
                    }
                }
            }

            .sr-breakdown {
                margin-top: 5px;
            }

            .close-modal-btn {
                cursor: pointer;
                position: absolute;
                right: 20px;
                top: 20px;
            }

            .example-note {
                display: inline-block;
                margin-top: 10px;
            }

            .rank {
                vertical-align: middle;
            }
        }

        .equation {
            display: flex;
            width: 100%;
            margin: 10px 0 0 12px;

            > * {
                align-self: center;
            }

            .fraction {
                display: flex;
                flex-direction: column;
            }

            .equals-sign {
                margin: 0 15px;
            }

            .numerator {
                text-align: center;
                border-bottom: 1px solid black;
            }

            .denominator {
                display: flex;
                align-self: center;

                > * {
                    align-self: center;
                }
            }

            .power {
                margin-top: 9px;
            }

            sup {
                font-size: .65em;
                vertical-align: super;
                margin-left: 2px;
            }

            sub {
                vertical-align: sub;
            }

        }
    }

    @media only screen and (min-width: $screen-sm) {

        .modal {

            &.skill-rating {
                min-width: 500px;
            }
        }
    }

</style>
